import * as React from 'react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { getActiveRegistryVotingRequest } from 'api/asset-registry';
import { getActiveProposalsCount } from 'api/governance';

import { AppRoutes } from 'constants/routes';

import { getAssetString, getEnvClassicAssetData } from 'helpers/assets';
import { createLumen } from 'helpers/token';

import useAuthStore from 'store/authStore/useAuthStore';

import { ModalService } from 'services/globalServices';

import { Proposal } from 'types/governance';

import ChooseLoginMethodModal from 'web/modals/auth/ChooseLoginMethodModal';

import AquaLogo from 'assets/aqua/aqua-logo-text.svg';
import IconProfile from 'assets/icons/nav/icon-profile.svg';

import ActiveAssetProposal from 'components/Header/ActiveAssetProposal/ActiveAssetProposal';
import { ActiveProposals } from 'components/Header/ActiveProposals/ActiveProposals';
import ExpandedMenu from 'components/Header/ExpandedMenu/ExpandedMenu';

import { commonMaxWidth, respondDown } from 'styles/mixins';
import { Breakpoints, COLORS, HEADER_HEIGHT, Z_INDEX } from 'styles/style-constants';

import AccountBlock from './AccountBlock';
import NotificationsBlock from './NotificationsBlock';

const Container = styled.header`
    ${commonMaxWidth};
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: ${HEADER_HEIGHT};
    padding: 0 4rem;
    z-index: ${Z_INDEX.header};
    background: ${COLORS.white};

    ${respondDown(Breakpoints.md)`
        height: 5.8rem;
        padding: 0 1.6rem;
        z-index: ${Z_INDEX.accountMenu}
    `}
`;

const Aqua = styled(AquaLogo)`
    height: 4.4rem;
    color: ${COLORS.textPrimary};

    ${respondDown(Breakpoints.md)`
        height: 3.4rem;
    `}
`;

export const linkStyles = css`
    text-decoration: none;
    font-size: 1.6rem;
    line-height: 2.4rem;
    white-space: nowrap;

    &:hover {
        color: ${COLORS.purple500} !important;
    }
`;

const NavLinkStyled = styled(NavLink)<{ $disabled?: boolean }>`
    color: ${({ $disabled }) => ($disabled ? COLORS.textGray : COLORS.textPrimary)}!important;
    font-weight: ${({ $disabled }) => ($disabled ? '400!important' : 'unset')};
    ${linkStyles};

    &:not(:last-child) {
        margin-right: 2.4rem;
    }

    ${respondDown(Breakpoints.lg)`
        margin-right: 1rem;
    `}
`;

const NavLinkWithCount = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 1.2rem;

    a {
        margin: 0 !important;
    }

    ${respondDown(Breakpoints.md)`
        margin-bottom: 2.4rem;
    `}
`;

const Divider = styled.div`
    height: 2.4rem;
    width: 0;
    border-left: 0.1rem solid ${COLORS.gray100};
    margin: 0 2.4rem;

    ${respondDown(Breakpoints.lg)`
        margin-right: 1rem;
        height: 2rem;
    `};

    ${respondDown(Breakpoints.md)`
         border-left: none;
         height: 0;
         width: 20rem;
         margin: 2.4rem 0;
         border-top: 0.1rem solid ${COLORS.gray100};
    `};
`;

const MainLink = styled(NavLink)`
    height: 4.4rem;

    ${respondDown(Breakpoints.md)`
        height: 3.4rem;
    `}
`;

const NavLinks = styled.div`
    display: flex;
    padding: 1.6rem;
    align-items: center;

    a {
        color: ${COLORS.textPrimary};
        text-decoration: none;

        &:hover {
            color: ${COLORS.purple500};
        }

        &::before {
            content: attr(title);
            visibility: hidden;
            overflow: hidden;
            user-select: none;
            pointer-events: none;
            font-weight: 700;
            height: 0;
            display: block;
        }
    }

    ${respondDown(Breakpoints.xl)`
        a {
            &:not(:last-child) {
                margin-right: 2.2rem;
            }
        }
    `}

    ${respondDown(Breakpoints.lg)`
        a {
            &:not(:last-child) {
                margin-right: 2rem;
            }
        }
    `}

    ${respondDown(Breakpoints.md)`
        display: none;
    `}
`;

const RightBlock = styled.div`
    display: flex;
    align-items: center;
`;

const MyAquarius = styled(NavLink)`
    display: flex;
    align-items: center;
    padding: 0 1.6rem;
    height: 4.8rem;
    background-color: ${COLORS.gray50};
    border-radius: 0.6rem;
    cursor: pointer;
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: ${COLORS.textPrimary};
    text-decoration: none;

    div::after {
        content: attr(title);
        visibility: hidden;
        overflow: hidden;
        user-select: none;
        pointer-events: none;
        font-weight: 700;
        height: 0;
        display: block;
    }

    svg {
        margin-right: 0.8rem;
        height: 1.6rem;
        width: 1.6rem;
    }

    &:hover {
        color: ${COLORS.purple500};
    }

    ${respondDown(Breakpoints.xl)`
        width: 4.8rem;
        div {
            display: none;
        }
        svg {
            margin-right: 0;
        }
    `}
`;

const Links = () => {
    const [proposalsCounts, setProposalsCounts] = useState({ active: 0, discussion: 0 });
    const [activeAssetProposal, setActiveAssetProposal] = useState<Proposal | null>(null);

    useEffect(() => {
        getActiveProposalsCount().then(res => {
            setProposalsCounts(res);
        });

        getActiveRegistryVotingRequest()
            .then(setActiveAssetProposal)
            .catch(() => {
                setActiveAssetProposal(null);
            });
    }, []);

    const activeAssetIndicator = activeAssetProposal ? (
        <ActiveAssetProposal proposal={activeAssetProposal} />
    ) : null;

    return (
        <>
            <NavLinkStyled
                to={AppRoutes.section.amm.link.index}
                style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                title="Pools"
            >
                Pools
            </NavLinkStyled>
            <NavLinkStyled
                to={AppRoutes.section.swap.to.index({
                    source: getAssetString(createLumen()),
                    destination: getEnvClassicAssetData('aqua').assetString,
                })}
                style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                title="Swap"
            >
                Swap
            </NavLinkStyled>

            <Divider />

            <ExpandedMenu
                title="Incentives"
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.page.rewards}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="AQUA Rewards"
                        >
                            AQUA Rewards
                        </NavLinkStyled>
                        <NavLinkStyled
                            to={AppRoutes.section.incentive.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Pool Incentives"
                        >
                            Pool Incentives
                        </NavLinkStyled>
                        <NavLinkStyled
                            to={AppRoutes.section.bribes.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Voting Incentives"
                        >
                            Voting Incentives
                        </NavLinkStyled>
                    </>
                }
            />

            <ExpandedMenu
                title="DAO"
                counts={proposalsCounts}
                indicator={activeAssetIndicator}
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.page.vote}
                            exact="true"
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Liquidity Voting"
                        >
                            Liquidity Voting
                        </NavLinkStyled>

                        <NavLinkWithCount>
                            <NavLinkStyled
                                to={AppRoutes.section.assetRegistry.link.index}
                                style={({ isActive }) =>
                                    isActive ? { fontWeight: 700 } : undefined
                                }
                                title="Asset Registry"
                            >
                                Asset Registry
                            </NavLinkStyled>
                            {activeAssetIndicator}
                        </NavLinkWithCount>

                        <NavLinkWithCount>
                            <NavLinkStyled
                                to={AppRoutes.section.governance.link.index}
                                style={({ isActive }) =>
                                    isActive ? { fontWeight: 700 } : undefined
                                }
                                title="DAO Proposals"
                            >
                                DAO Proposals
                            </NavLinkStyled>
                            <ActiveProposals
                                discussionCount={proposalsCounts.discussion}
                                activeCount={proposalsCounts.active}
                            />
                        </NavLinkWithCount>

                        <NavLinkStyled
                            to={AppRoutes.section.delegate.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="ICE delegation"
                        >
                            ICE Delegation
                        </NavLinkStyled>
                    </>
                }
            />

            <ExpandedMenu
                title="AQUA & ICE"
                links={
                    <>
                        <NavLinkStyled
                            to={AppRoutes.section.locker.link.index}
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="Lock AQUA"
                        >
                            Lock AQUA
                        </NavLinkStyled>
                        <NavLinkStyled
                            style={({ isActive }) => (isActive ? { fontWeight: 700 } : undefined)}
                            title="About"
                            to={AppRoutes.page.token}
                        >
                            About
                        </NavLinkStyled>
                    </>
                }
            />
        </>
    );
};

const Header = (): React.ReactNode => {
    const { isLogged } = useAuthStore();

    const onMyAquariusClick = (e: React.MouseEvent) => {
        if (!isLogged) {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();

            return ModalService.openModal(ChooseLoginMethodModal, {
                redirectURL: AppRoutes.page.account,
            });
        }
    };
    return (
        <Container>
            <MainLink to={AppRoutes.page.main}>
                <Aqua />
            </MainLink>

            <NavLinks>
                <Links />
            </NavLinks>

            <RightBlock>
                <MyAquarius onClick={onMyAquariusClick} to={AppRoutes.page.account}>
                    <IconProfile />
                    <div>Dashboard</div>
                </MyAquarius>
                <NotificationsBlock />
                <AccountBlock navLinks={<Links />} />
            </RightBlock>
        </Container>
    );
};

export default Header;
